/**
 * System Analytics Panel - Compact Version with Performance Graph
 * ================================================================
 * Displays operational metrics in a compact 2-3 column grid layout.
 * Includes Performance graph for executor stats filtered by group.
 * Includes Video Approval charts for management users.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userManagementApi } from '../../api/userManagement';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { EMZI_COLORS, EMZI_SHADOWS } from '../../styles/emziCI';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

// Import Video Approval Charts
import VideoApprovalComparison from '../dashboard/VideoApprovalComparison';
import VideoMonthlyTrend from '../dashboard/VideoMonthlyTrend';

// Chart colors
const CHART_COLORS = {
  submissions: '#3B82F6',
  approved: '#16A34A',
  videos: '#8B5CF6'
};

// Compact Detail Popup Component
const DetailPopup = ({ isOpen, onClose, title, icon, children }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: EMZI_COLORS.overlay }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-sm w-full max-h-[70vh] overflow-y-auto"
        style={{ boxShadow: EMZI_SHADOWS.lg }}
        onClick={e => e.stopPropagation()}
      >
        <div 
          className="sticky top-0 flex items-center justify-between px-3 py-2 border-b"
          style={{ backgroundColor: EMZI_COLORS.primary }}
        >
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            {icon} {title}
          </h3>
          <button 
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full p-1 transition-colors text-sm"
          >
            ✕
          </button>
        </div>
        <div className="p-3">
          {children}
        </div>
      </div>
    </div>
  );
};

// Custom tooltip for bar chart
const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-2 rounded-lg shadow-lg border border-gray-200 text-xs">
        <p className="font-bold text-gray-800 mb-1">{data.fullName || label}</p>
        <div className="space-y-0.5">
          <p className="text-blue-600">📊 Submissions: <span className="font-bold">{data.submissions}</span></p>
          <p className="text-green-600">✅ SB Approved: <span className="font-bold">{data.approved}</span></p>
          <p className="text-purple-600">🎬 Vid Approved: <span className="font-bold">{data.videos}</span></p>
        </div>
      </div>
    );
  }
  return null;
};

// Compact Stat Card Component
const StatCard = ({ value, label, icon, onClick, highlight, color }) => (
  <Card 
    className="cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
    style={{ 
      borderColor: color || EMZI_COLORS.primary,
      borderWidth: '2px',
      boxShadow: EMZI_SHADOWS.sm
    }}
    onClick={onClick}
  >
    <CardContent className="p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <span className="text-xs" style={{ color: EMZI_COLORS.textSecondary }}>{label}</span>
      </div>
      <div 
        className="text-2xl font-bold"
        style={{ color: color || (highlight ? EMZI_COLORS.success : EMZI_COLORS.primary) }}
      >
        {value}
      </div>
    </CardContent>
  </Card>
);

const SystemAnalyticsPanel = () => {
  const { user } = useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [governanceData, setGovernanceData] = useState(null);
  const [executorData, setExecutorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [activePopup, setActivePopup] = useState(null);
  const [performanceView, setPerformanceView] = useState('overview'); // 'overview' | 'leaderboard' | 'cards'

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [dashboardStats, executorPerf] = await Promise.all([
        userManagementApi.getManagementDashboardStats(),
        userManagementApi.getExecutorPerformance()
      ]);
      
      let govData = { events_24h: 0, compliance_blocks: 0, health_status: 'active' };
      try {
        govData = await userManagementApi.getGovernanceSummary();
      } catch (govError) {
        console.log('Governance data not available:', govError);
      }

      // Transform dashboard stats
      const analyticsFormatted = {
        total_submissions: dashboardStats?.total_submissions || 0,
        compliance_rate: dashboardStats?.compliance_rate || 0,
        approval_latency: calculateApprovalLatency(dashboardStats),
        total_bp: formatBP(dashboardStats?.total_bp || 0),
        pending_count: dashboardStats?.pending_count || 0,
        approved_count: dashboardStats?.approved_count || 0,
        rejected_count: dashboardStats?.rejected_count || 0,
        executor_count: dashboardStats?.executor_count || 0,
        user_groups: dashboardStats?.user_groups || [],
        scope: dashboardStats?.scope || 'unknown',
        recent_submissions: dashboardStats?.recent_submissions || 0
      };

      setAnalyticsData(analyticsFormatted);
      setExecutorData(executorPerf);
      setGovernanceData({
        events_24h: govData?.health_indicators?.total_events_24h || govData?.events_24h || 0,
        compliance_blocks: govData?.health_indicators?.compliance_blocks_24h || govData?.compliance_blocks || 0,
        health_status: govData?.health_indicators?.alert_level === 'low' ? 'active' : govData?.health_status || 'active'
      });
      setLastRefresh(new Date());

    } catch (error) {
      console.error('Failed to load analytics:', error);
      setError(error.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const calculateApprovalLatency = (stats) => {
    if (stats?.approved_count > 0 && stats?.recent_submissions > 0) {
      return '2.5';
    }
    return '0';
  };

  const formatBP = (bp) => {
    if (typeof bp === 'string') return bp;
    if (bp >= 1000000) return `${(bp / 1000000).toFixed(1)}M`;
    if (bp >= 1000) return `${(bp / 1000).toFixed(1)}K`;
    return bp.toString();
  };

  // Bar chart data
  const barChartData = useMemo(() => {
    if (!executorData?.executors) return [];
    return executorData.executors.map(exec => ({
      name: (exec.executor_name || exec.user_id?.substring(0, 8) || 'Unknown').substring(0, 10),
      fullName: exec.executor_name || exec.user_id || 'Unknown',
      submissions: exec.total_submissions || 0,
      approved: exec.approved || 0,
      videos: exec.videos_approved || 0
    }));
  }, [executorData]);

  // Enhanced executors with calculated metrics
  const enhancedExecutors = useMemo(() => {
    if (!executorData?.executors) return [];
    
    return executorData.executors.map((exec, index) => {
      const total = exec.total_submissions || 0;
      const approved = exec.approved || 0;
      const videosApproved = exec.videos_approved || 0;
      
      const approvalRate = total > 0 ? Math.round((approved / total) * 100) : 0;
      const qualityScore = Math.round(approvalRate * 0.6 + (videosApproved > 0 ? 40 : 0));
      
      const colors = ['#0072E5', '#16A34A', '#F59E0B', '#DC2626', '#8B5CF6', '#06B6D4', '#EC4899'];
      
      return {
        ...exec,
        approvalRate,
        qualityScore,
        color: colors[index % colors.length]
      };
    }).sort((a, b) => b.qualityScore - a.qualityScore)
      .map((exec, index) => ({ ...exec, rank: index + 1 }));
  }, [executorData]);

  // Get medal for rank
  const getMedal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div 
            className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin mx-auto mb-2"
            style={{ borderColor: `${EMZI_COLORS.primary} transparent ${EMZI_COLORS.primary} ${EMZI_COLORS.primary}` }}
          ></div>
          <p className="text-xs" style={{ color: EMZI_COLORS.textSecondary }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-3">
        <Card style={{ borderColor: EMZI_COLORS.error }}>
          <CardContent className="p-3 text-center">
            <p className="text-sm mb-2" style={{ color: EMZI_COLORS.error }}>{error}</p>
            <Button onClick={loadAnalytics} size="sm" style={{ backgroundColor: EMZI_COLORS.primary }}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="text-xs" style={{ color: EMZI_COLORS.textSecondary }}>
          <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
          {analyticsData?.user_groups?.length > 0 && (
            <span className="ml-1">
              • Scope: <strong style={{ color: EMZI_COLORS.primary }}>{analyticsData.user_groups.join(', ')}</strong>
            </span>
          )}
        </div>
        <Button 
          onClick={loadAnalytics}
          size="sm"
          className="text-xs px-2 py-1"
          style={{ backgroundColor: EMZI_COLORS.primary, color: 'white' }}
        >
          🔄 Refresh
        </Button>
      </div>

      {/* Main Stats - 2x2 Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <StatCard
          icon="📊"
          value={analyticsData?.total_submissions || 0}
          label="Submissions"
          onClick={() => setActivePopup('submissions')}
        />
        
        <StatCard
          icon="✅"
          value={`${analyticsData?.compliance_rate || 0}%`}
          label="Compliance"
          highlight={analyticsData?.compliance_rate >= 90}
          color={analyticsData?.compliance_rate >= 90 ? EMZI_COLORS.success : EMZI_COLORS.primary}
          onClick={() => setActivePopup('compliance')}
        />
        
        <StatCard
          icon="⏱️"
          value={`${analyticsData?.approval_latency || 0}h`}
          label="Avg Latency"
          onClick={() => setActivePopup('latency')}
        />
        
        <StatCard
          icon="🧠"
          value={analyticsData?.total_bp || '0'}
          label="Brain Points"
          onClick={() => setActivePopup('brainpoints')}
        />
      </div>

      {/* Status Breakdown - 3 Column Inline */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
        <div 
          className="p-2 rounded-lg cursor-pointer hover:opacity-80 transition-opacity text-center"
          style={{ backgroundColor: `${EMZI_COLORS.success}15`, border: `1px solid ${EMZI_COLORS.success}40` }}
          onClick={() => setActivePopup('approved')}
        >
          <div className="text-lg font-bold" style={{ color: EMZI_COLORS.success }}>{analyticsData?.approved_count || 0}</div>
          <div className="text-[10px]" style={{ color: EMZI_COLORS.textSecondary }}>Approved</div>
        </div>
        <div 
          className="p-2 rounded-lg cursor-pointer hover:opacity-80 transition-opacity text-center"
          style={{ backgroundColor: `${EMZI_COLORS.warning}15`, border: `1px solid ${EMZI_COLORS.warning}40` }}
          onClick={() => setActivePopup('pending')}
        >
          <div className="text-lg font-bold" style={{ color: EMZI_COLORS.warning }}>{analyticsData?.pending_count || 0}</div>
          <div className="text-[10px]" style={{ color: EMZI_COLORS.textSecondary }}>Pending</div>
        </div>
        <div 
          className="p-2 rounded-lg cursor-pointer hover:opacity-80 transition-opacity text-center"
          style={{ backgroundColor: `${EMZI_COLORS.error}15`, border: `1px solid ${EMZI_COLORS.error}40` }}
          onClick={() => setActivePopup('rejected')}
        >
          <div className="text-lg font-bold" style={{ color: EMZI_COLORS.error }}>{analyticsData?.rejected_count || 0}</div>
          <div className="text-[10px]" style={{ color: EMZI_COLORS.textSecondary }}>Rejected</div>
        </div>
      </div>

      {/* ============ PERFORMANCE SECTION ============ */}
      {executorData?.executors?.length > 0 && (
        <Card className="overflow-hidden" style={{ borderColor: EMZI_COLORS.border }}>
          {/* Performance Header with View Toggle */}
          <CardHeader className="px-3 py-2 border-b bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm text-gray-900 flex items-center font-bold">
                📊 Performance
                <Badge className="ml-1 bg-blue-100 text-blue-700 font-bold text-[10px] px-1.5 py-0">
                  {executorData.executors.length}
                </Badge>
              </CardTitle>
              
              {/* View Toggle */}
              <div className="flex gap-0.5 bg-gray-100 rounded-md p-0.5">
                {[
                  { id: 'overview', icon: '📈' },
                  { id: 'leaderboard', icon: '🏆' },
                  { id: 'cards', icon: '📋' }
                ].map(view => (
                  <button
                    key={view.id}
                    onClick={() => setPerformanceView(view.id)}
                    className={`px-2 py-1 text-xs rounded transition-all ${
                      performanceView === view.id 
                        ? 'bg-white text-blue-600 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    {view.icon}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-3">
            {/* Overview View with Bar Chart */}
            {performanceView === 'overview' && (
              <div className="space-y-3">
                {/* Summary Stats - 3 columns */}
                <div className="flex gap-2">
                  <div className="flex-1 bg-blue-50 rounded-lg p-2 text-center">
                    <div className="text-base font-black text-blue-600">
                      {executorData.summary?.total_submissions || 0}
                    </div>
                    <div className="text-[10px] text-blue-700">Submissions</div>
                  </div>
                  <div className="flex-1 bg-green-50 rounded-lg p-2 text-center">
                    <div className="text-base font-black text-green-600">
                      {executorData.summary?.total_approved || 0}
                    </div>
                    <div className="text-[10px] text-green-700">SB Approved</div>
                  </div>
                  <div className="flex-1 bg-purple-50 rounded-lg p-2 text-center">
                    <div className="text-base font-black text-purple-600">
                      {executorData.summary?.total_videos_approved || 0}
                    </div>
                    <div className="text-[10px] text-purple-700">Vid Approved</div>
                  </div>
                </div>

                {/* Bar Chart */}
                <div className="bg-gray-50 rounded-xl p-3">
                  <h4 className="text-xs font-bold text-gray-700 mb-2">📊 Performance by Executor</h4>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={barChartData} margin={{ top: 5, right: 5, left: -15, bottom: 15 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 9, fill: '#666' }}
                        angle={-45}
                        textAnchor="end"
                        height={45}
                      />
                      <YAxis tick={{ fontSize: 9, fill: '#666' }} />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Bar dataKey="submissions" fill={CHART_COLORS.submissions} name="Submissions" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="approved" fill={CHART_COLORS.approved} name="SB Approved" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="videos" fill={CHART_COLORS.videos} name="Vid Approved" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  {/* Legend */}
                  <div className="flex justify-center gap-3 mt-2">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-sm"></div>
                      <span className="text-[9px] text-gray-500">Submissions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-600 rounded-sm"></div>
                      <span className="text-[9px] text-gray-500">SB Approved</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-purple-500 rounded-sm"></div>
                      <span className="text-[9px] text-gray-500">Vid Approved</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Leaderboard View */}
            {performanceView === 'leaderboard' && (
              <div className="space-y-1.5">
                {enhancedExecutors.map((executor, index) => (
                  <div
                    key={executor.user_id}
                    className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
                      index === 0 ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200' :
                      index === 1 ? 'bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200' :
                      index === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200' :
                      'bg-gray-50'
                    }`}
                  >
                    {/* Rank */}
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-400 text-orange-900' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {getMedal(executor.rank)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-800 text-xs truncate">
                        {executor.executor_name || executor.user_id?.substring(0, 8) || 'Executor'}
                      </div>
                      <div className="flex gap-1 mt-0.5">
                        <Badge className="bg-green-100 text-green-700 text-[10px] px-1 py-0">
                          {executor.approvalRate}%
                        </Badge>
                        <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1 py-0">
                          {executor.total_submissions}
                        </Badge>
                      </div>
                    </div>

                    {/* Quality Score */}
                    <div className="text-right">
                      <div className="text-lg font-black" style={{ color: executor.color }}>
                        {executor.qualityScore}
                      </div>
                      <div className="text-[10px] text-gray-400">Score</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cards View */}
            {performanceView === 'cards' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {enhancedExecutors.map((executor) => (
                  <div
                    key={executor.user_id}
                    className="bg-gray-50 rounded-lg p-2 border border-gray-100"
                  >
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: executor.color }}
                      >
                        {(executor.executor_name || executor.user_id || '?')?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-gray-800 text-[11px] truncate">
                          {executor.executor_name || executor.user_id?.substring(0, 8) || 'Executor'}
                        </div>
                        <div className="text-[9px] text-gray-400">{getMedal(executor.rank)}</div>
                      </div>
                      <div className="text-sm font-black" style={{ color: executor.color }}>
                        {executor.qualityScore}%
                      </div>
                    </div>

                    {/* Mini Stats - 3 columns */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
                      <div className="text-center">
                        <div className="text-xs font-bold text-gray-800">{executor.total_submissions || 0}</div>
                        <div className="text-[8px] text-gray-400">Total</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-bold text-green-600">{executor.approved || 0}</div>
                        <div className="text-[8px] text-gray-400">✅</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-bold text-purple-600">{executor.videos_approved || 0}</div>
                        <div className="text-[8px] text-gray-400">🎬</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Video Approval Charts - For Management Users */}
      <VideoApprovalComparison />
      <VideoMonthlyTrend />

      {/* Governance & Team - 2 Column */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {/* Governance */}
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          style={{ borderColor: EMZI_COLORS.primary, borderWidth: '1px' }}
          onClick={() => setActivePopup('governance')}
        >
          <CardHeader className="p-2 pb-1">
            <CardTitle className="text-xs font-semibold flex items-center gap-1">
              🔐 Governance
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 space-y-1">
            <div className="flex justify-between text-xs">
              <span style={{ color: EMZI_COLORS.textSecondary }}>Events 24h</span>
              <span className="font-semibold">{governanceData?.events_24h || 0}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: EMZI_COLORS.textSecondary }}>Blocks</span>
              <span className="font-semibold" style={{ color: governanceData?.compliance_blocks > 0 ? EMZI_COLORS.warning : EMZI_COLORS.success }}>
                {governanceData?.compliance_blocks || 0}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: EMZI_COLORS.textSecondary }}>Health</span>
              <Badge 
                className="text-[10px] px-1 py-0"
                style={{ backgroundColor: EMZI_COLORS.success, color: 'white' }}
              >
                {governanceData?.health_status || 'active'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Team */}
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          style={{ borderColor: EMZI_COLORS.primary, borderWidth: '1px' }}
          onClick={() => setActivePopup('team')}
        >
          <CardHeader className="p-2 pb-1">
            <CardTitle className="text-xs font-semibold flex items-center gap-1">
              👥 Team
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 space-y-1">
            <div className="flex justify-between text-xs">
              <span style={{ color: EMZI_COLORS.textSecondary }}>Executors</span>
              <span className="font-semibold">{analyticsData?.executor_count || 0}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: EMZI_COLORS.textSecondary }}>Groups</span>
              <span className="font-semibold">{analyticsData?.user_groups?.length || 0}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: EMZI_COLORS.textSecondary }}>Scope</span>
              <Badge 
                className="text-[10px] px-1 py-0"
                style={{ backgroundColor: EMZI_COLORS.primary, color: 'white' }}
              >
                {analyticsData?.scope === 'functional' ? 'Group' : 'Global'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ============ POPUP MODALS ============ */}
      
      {/* Submissions Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'submissions'}
        onClose={() => setActivePopup(null)}
        title="Total Submissions"
        icon="📊"
      >
        <div className="space-y-3">
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
            <div className="text-3xl font-bold" style={{ color: EMZI_COLORS.primary }}>
              {analyticsData?.total_submissions || 0}
            </div>
            <div className="text-xs" style={{ color: EMZI_COLORS.textSecondary }}>Total submissions</div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
            <div className="p-2 rounded-lg text-center" style={{ backgroundColor: `${EMZI_COLORS.success}15` }}>
              <div className="text-lg font-bold" style={{ color: EMZI_COLORS.success }}>{analyticsData?.approved_count || 0}</div>
              <div className="text-[10px]">Approved</div>
            </div>
            <div className="p-2 rounded-lg text-center" style={{ backgroundColor: `${EMZI_COLORS.warning}15` }}>
              <div className="text-lg font-bold" style={{ color: EMZI_COLORS.warning }}>{analyticsData?.pending_count || 0}</div>
              <div className="text-[10px]">Pending</div>
            </div>
            <div className="p-2 rounded-lg text-center" style={{ backgroundColor: `${EMZI_COLORS.error}15` }}>
              <div className="text-lg font-bold" style={{ color: EMZI_COLORS.error }}>{analyticsData?.rejected_count || 0}</div>
              <div className="text-[10px]">Rejected</div>
            </div>
          </div>
        </div>
      </DetailPopup>

      {/* Compliance Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'compliance'}
        onClose={() => setActivePopup(null)}
        title="Compliance Rate"
        icon="✅"
      >
        <div className="space-y-3">
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
            <div className="text-3xl font-bold" style={{ color: analyticsData?.compliance_rate >= 90 ? EMZI_COLORS.success : EMZI_COLORS.warning }}>
              {analyticsData?.compliance_rate || 0}%
            </div>
            <div className="text-xs" style={{ color: EMZI_COLORS.textSecondary }}>Approval rate</div>
          </div>
        </div>
      </DetailPopup>

      {/* Latency Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'latency'}
        onClose={() => setActivePopup(null)}
        title="Approval Latency"
        icon="⏱️"
      >
        <div className="space-y-3">
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
            <div className="text-3xl font-bold" style={{ color: EMZI_COLORS.primary }}>
              {analyticsData?.approval_latency || 0} <span className="text-sm">hrs</span>
            </div>
            <div className="text-xs" style={{ color: EMZI_COLORS.textSecondary }}>Avg time to decision</div>
          </div>
        </div>
      </DetailPopup>

      {/* Brain Points Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'brainpoints'}
        onClose={() => setActivePopup(null)}
        title="Brain Points"
        icon="🧠"
      >
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
          <div className="text-3xl font-bold" style={{ color: EMZI_COLORS.primary }}>
            {analyticsData?.total_bp || 0}
          </div>
          <div className="text-xs" style={{ color: EMZI_COLORS.textSecondary }}>Total BP earned</div>
        </div>
      </DetailPopup>

      {/* Approved Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'approved'}
        onClose={() => setActivePopup(null)}
        title="Approved"
        icon="✅"
      >
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${EMZI_COLORS.success}15` }}>
          <div className="text-3xl font-bold" style={{ color: EMZI_COLORS.success }}>
            {analyticsData?.approved_count || 0}
          </div>
          <div className="text-xs mt-1" style={{ color: EMZI_COLORS.textSecondary }}>
            Submissions approved
          </div>
        </div>
      </DetailPopup>

      {/* Pending Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'pending'}
        onClose={() => setActivePopup(null)}
        title="Pending"
        icon="⏳"
      >
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${EMZI_COLORS.warning}15` }}>
          <div className="text-3xl font-bold" style={{ color: EMZI_COLORS.warning }}>
            {analyticsData?.pending_count || 0}
          </div>
          <div className="text-xs mt-1" style={{ color: EMZI_COLORS.textSecondary }}>
            Awaiting review
          </div>
        </div>
      </DetailPopup>

      {/* Rejected Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'rejected'}
        onClose={() => setActivePopup(null)}
        title="Rejected"
        icon="❌"
      >
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: `${EMZI_COLORS.error}15` }}>
          <div className="text-3xl font-bold" style={{ color: EMZI_COLORS.error }}>
            {analyticsData?.rejected_count || 0}
          </div>
          <div className="text-xs mt-1" style={{ color: EMZI_COLORS.textSecondary }}>
            Submissions rejected
          </div>
        </div>
      </DetailPopup>

      {/* Governance Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'governance'}
        onClose={() => setActivePopup(null)}
        title="Governance"
        icon="🔐"
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
            <div className="text-sm font-medium">Events (24h)</div>
            <div className="text-xl font-bold" style={{ color: EMZI_COLORS.primary }}>{governanceData?.events_24h || 0}</div>
          </div>
          <div className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
            <div className="text-sm font-medium">Compliance Blocks</div>
            <div className="text-xl font-bold" style={{ color: governanceData?.compliance_blocks > 0 ? EMZI_COLORS.warning : EMZI_COLORS.success }}>
              {governanceData?.compliance_blocks || 0}
            </div>
          </div>
          <div className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
            <div className="text-sm font-medium">System Health</div>
            <Badge style={{ backgroundColor: EMZI_COLORS.success, padding: '4px 8px' }}>
              {governanceData?.health_status || 'Active'}
            </Badge>
          </div>
        </div>
      </DetailPopup>

      {/* Team Detail Popup */}
      <DetailPopup
        isOpen={activePopup === 'team'}
        onClose={() => setActivePopup(null)}
        title="Team Overview"
        icon="👥"
      >
        <div className="space-y-3">
          <div className="text-center p-3 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
            <div className="text-3xl font-bold" style={{ color: EMZI_COLORS.primary }}>
              {analyticsData?.executor_count || 0}
            </div>
            <div className="text-xs" style={{ color: EMZI_COLORS.textSecondary }}>Executors in group(s)</div>
          </div>
          
          {analyticsData?.user_groups?.length > 0 && (
            <div className="p-2 rounded-lg" style={{ backgroundColor: EMZI_COLORS.surface }}>
              <div className="text-xs font-medium mb-2">Your Groups:</div>
              <div className="flex flex-wrap gap-1">
                {analyticsData.user_groups.map((group, i) => (
                  <Badge key={`item-${i}`} style={{ backgroundColor: EMZI_COLORS.primary, fontSize: '11px', padding: '4px 8px' }}>
                    {group}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </DetailPopup>
    </div>
  );
};

export default SystemAnalyticsPanel;
